import { Services, checkHostnames, isEmpty, joinURLPath, payloadValidator, sendingsAssociationByType, sendingsDefaultTextByType, sendingsTestPayload } from '@sigma-frontend-libs/commons'
import { ETTSTypes, SendingTypes } from '@sigmasms/shared-types'

import { catchSymbolsMenus, globalErrorHandler, hostnames, proxy, request } from '@/utils'

import { normalizePayload } from '@/components/services/sendingsCreate/utils'

import render from './render'

export const voiceModes = {
  generate: 'generate',
  upload: 'upload'
}

export default {
  name: 'SendingsCreateForm',

  mixins: [ proxy() ],

  props: {
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },

    template: {
      type: Object,
      default: () => {
        return {}
      }
    },
    applyingTemplate: {
      type: Boolean,
      default: false
    },

    service: {
      type: String,
      default: undefined
    },

    isFallback: {
      type: Boolean,
      default: false
    },

    errors: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },

  data() {
    return {
      sendernamesError: false,

      voiceMode: voiceModes.generate,

      ttsLoading: false,
      ttsFile: undefined,
      ttsCooldown: undefined,
      ttsInterval: undefined
    }
  },

  computed: {
    types() {
      switch (this.service) {
        case Services.patterns: return this.getSendingTypesByPermission(`advanced.${Services.patterns}.create`)
        default: return this.sendingTypes
      }
    },

    validation() {
      return payloadValidator(this.proxy.payload, this.proxy.type)
    },

    checkTestRoute() {
      if (!this.service) {
        const entry = sendingsAssociationByType[this.proxy.type]
        if (entry) {
          return this.checkSomeId(entry.association, entry.id)
        }
      }

      return false
    },

    customProps() {
      switch (this.service) {
        case Services.patterns: {
          return {
            tags: catchSymbolsMenus['#'],
            customMenu: catchSymbolsMenus.custom['#']
          }
        }
        default: {
          return {
            tags: catchSymbolsMenus.$,
            customMenu: catchSymbolsMenus.custom.$
          }
        }
      }
    },

    showInfo() {
      return [ SendingTypes.viber, SendingTypes.voice, SendingTypes.flashcall ].includes(this.proxy.type)
    },

    showAttach() {
      return [ SendingTypes.viber, SendingTypes.whatsapp, SendingTypes.voice ].includes(this.proxy.type)
    },

    textDisabled() {
      return this.ttsLoading || !isEmpty(this.proxy.payload.audio)
    },

    attachDisabled() {
      return !!this.ttsFile || !isEmpty(this.proxy.payload.audio) || !isEmpty(this.proxy.payload.file) || !isEmpty(this.proxy.payload.image)
    },

    ttsDisabled() {
      return this.ttsLoading ||
         this.ttsCooldown > 0 ||
         isEmpty(this.proxy.payload.text) ||
         isEmpty(this.proxy.payload.tts)
    },

    voiceTabs() {
      return Object.values(voiceModes).map(value => {
        return {
          value,
          label: this.getTranslate(`misc.buttons.${value}`)
        }
      })
    }
  },

  watch: {
    // Без immediate: иначе тип выставляется до регистрации watcher-а
    // 'proxy.type' и начальный переход типа не срабатывает - applyTestPayload
    // не применяется при открытии страницы. Начальную установку типа делает
    // setSendingType строго в mounted: хук mounted миксина proxy
    // (receiveValue) заменяет proxy родительским value и затирает более
    // ранние правки (например из created), а mounted компонента выполняется
    // после хуков миксина.
    types: {
      handler() {
        this.setSendingType()
      },
      deep: true
    },

    // После полного сброса type проходит путь value -> '' -> value.
    // Восстанавливаем тип в отдельном цикле, чтобы query селектора имени
    // отправителя изменился и запустил запрос актуального списка.
    async 'proxy.type'(value) {
      if (isEmpty(value)) {
        await this.$nextTick()
        this.setSendingType()

        return
      }

      if (this.applyingTemplate) {
        return
      }

      this.resetPayloadByType(value)
      this.applyDefaultTextByType(value)
      this.applyTestPayload()
    },

    template: {
      handler(value) {
        if (value) {
          this.handleVoiceModeByTemplate(value)
        }
      },
      deep: true
    },

    voiceMode(value) {
      if (this.applyingTemplate) {
        return
      }

      this.handleVoicePayload(value)
    },

    validation(value) {
      this.$emit('validation', value)
    }
  },

  async mounted() {
    this.stopTTSCooldown()
    this.setSendingType()

    // Повторная установка типа после того, как реактивность устаканится.
    // Во вложенных формах (паттерны, шаблоны) родительский proxy в своем
    // mounted-хуке (receiveValue) выполняется после mounted дочерней формы и
    // затирает только что выставленный тип родительским value (type: '').
    // nextTick гарантирует, что setSendingType отработает последним и тип
    // закрепится. Для standalone-формы это no-op: тип уже выставлен.
    this.$nextTick(() => {
      this.setSendingType()
    })

    if (!isEmpty(this.proxy.payload.audio)) {
      this.voiceMode = voiceModes.upload
    }

    this.$emit('validation', this.validation)
  },

  beforeDestroy() {
    this.stopTTSCooldown()
  },

  methods: {
    checkSomeId(association, value) {
      return Array.isArray(this.account[association]) && this.account[association].length > 0 && this.account[association].some(({ id }) => {
        return id === value
      })
    },

    handleSendernamesState(hasSendernames) {
      if (hasSendernames) {
        this.sendernamesError = false
      } else {
        this.sendernamesError = true
      }
    },

    resetPayloadByType(type) {
      this.sendernamesError = false

      // Вложенные fallback-формы редактора шаблонов получают service ради
      // запрета подстановки дефолтного текста и нормализуются как рассылка.
      if (this.service === Services.templates && !this.isFallback) {
        this.proxy.payload = normalizePayload(type, this.proxy.payload)

        return
      }

      // Текущий payload переживает смену типа: normalizePayload отбрасывает
      // недопустимые для нового типа поля и сохраняет допустимые (text, а также
      // image между viber и whatsapp). Отправитель всегда сбрасывается.
      const preservedPayload = {
        ...this.proxy.payload ?? {},
        sender: ''
      }
      if (this.isFallback) {
        this.proxy.payload = normalizePayload(type, preservedPayload)
        this.ttsFile = undefined

        return
      }

      this.proxy.payload = normalizePayload(type, preservedPayload)
      this.ttsFile = undefined
      this.voiceMode = voiceModes.generate
    },

    applyDefaultTextByType(type) {
      if (this.service) {
        return
      }
      if (!checkHostnames(hostnames)) {
        return
      }
      if (isEmpty(sendingsDefaultTextByType[type])) {
        return
      }

      this.proxy.payload.text = sendingsDefaultTextByType[type]
    },

    applyTestPayload() {
      const test = sendingsTestPayload[this.proxy.type]
      if (!test) {
        return
      }

      if (this.checkTestRoute) {
        if (checkHostnames(hostnames)) {
          this.proxy.payload.sender = test.sender
          this.proxy.payload.text = test.text

          if (!isEmpty(test.tts)) {
            this.proxy.payload.tts = test.tts
          }
        }
      }
    },

    setSendingType() {
      if (isEmpty(this.proxy.type) && this.types?.length > 0) {
        this.proxy.type = this.types[0]
      }
    },

    stopTTSCooldown() {
      if (this.ttsInterval) {
        clearInterval(this.ttsInterval)
        this.ttsInterval = undefined
      }
      this.ttsCooldown = undefined
    },

    startTTSCooldown(seconds) {
      this.stopTTSCooldown()
      this.ttsCooldown = Number(seconds)
      this.ttsInterval = setInterval(() => {
        if (this.ttsCooldown > 0) {
          this.ttsCooldown--
        }
        if (this.ttsCooldown <= 0) {
          this.stopTTSCooldown()
        }
      }, 1000)
    },

    async generateTTS() {
      if (this.ttsDisabled) {
        return
      }
      try {
        this.ttsLoading = true
        const response = await request.create(joinURLPath(Services.tts, 'generate'), {
          body: {
            tts: this.proxy.payload.tts,
            text: this.proxy.payload.text
          }
        })

        const { fileId } = response || {}
        if (fileId) {
          const fileResponse = await request.get(joinURLPath(Services.storage, fileId), undefined, { return: 'meta' })
          if (fileResponse) {
            this.ttsFile = fileResponse
            if (!isEmpty(this.proxy.payload.audio)) {
              this.$delete(this.proxy.payload, 'audio')
            }
          }
        }
      } catch (error) {
        globalErrorHandler(error)
        if (error && error.error === 429 && error['retry-after']) {
          this.startTTSCooldown(error['retry-after'])
        }
      } finally {
        this.ttsLoading = false
      }
    },

    resetTTSFileOnChange() {
      if (this.voiceMode === voiceModes.generate) {
        this.ttsFile = undefined
      }
    },

    handleVoiceModeByTemplate(template) {
      if (template.type === SendingTypes.voice) {
        if (!isEmpty(template.payload.audio)) {
          this.voiceMode = voiceModes.upload
        } else {
          this.voiceMode = voiceModes.generate
        }
      }
    },

    handleVoicePayload(mode) {
      this.ttsFile = undefined
      if (mode === voiceModes.upload) {
        this.$delete(this.proxy.payload, 'text')
        this.$delete(this.proxy.payload, 'tts')
      } else {
        this.$delete(this.proxy.payload, 'audio')
        this.$set(this.proxy.payload, 'text', '')
        this.$set(this.proxy.payload, 'tts', ETTSTypes['yandex:filipp'])
      }
    },

    attachHandler({ type, id }) {
      if (id) {
        this.$set(this.proxy.payload, type, id)
      } else {
        this.ttsFile = undefined
        this.$delete(this.proxy.payload, type)
      }
    }
  },

  render
}
